/**
 * The running backend's release identity, from GET /api/get_version (SCHED-658).
 *
 * Fetched once per page load and shared by every version label on the page; a
 * failure is remembered for RETRY_AFTER_MS so a copy attempt can try again
 * without hammering a backend that is down. Plain fetch, no credentials: the
 * endpoint is public and must not go through the axios refresh interceptors.
 */
import { normaliseBackendRelease } from "utilities/supportDetails";

const RETRY_AFTER_MS = 30 * 1000;

let cached = null;
let inflight = null;
let failedAt = 0;

/** What is known right now (null when not fetched yet or unavailable). */
export function getBackendRelease() {
    return cached;
}

/** Resolves with the release, or null when the endpoint is unavailable. Never rejects. */
export function fetchBackendRelease() {
    if (cached) return Promise.resolve(cached);
    if (inflight) return inflight;
    if (failedAt && Date.now() - failedAt < RETRY_AFTER_MS) return Promise.resolve(null);
    if (typeof fetch !== "function") return Promise.resolve(null);

    const base = (process.env.NEXT_PUBLIC_BACKEND_PATH || "").replace(/\/+$/, "");
    inflight = fetch(`${base}/api/get_version`, { method: "GET", credentials: "omit", headers: { Accept: "application/json" } })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
            cached = normaliseBackendRelease(data);
            if (!cached) failedAt = Date.now();
            return cached;
        })
        .catch(() => {
            failedAt = Date.now();
            return null;
        })
        .finally(() => {
            inflight = null;
        });
    return inflight;
}

/** Tests only. */
export function resetBackendRelease() {
    cached = null;
    inflight = null;
    failedAt = 0;
}
